import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { type IRetrieveWorkspaceQuery, type IUpdateWorkspaceCommand } from '@telespot/management/use-cases';
import { AuthService } from '@telespot/web-core';
import { WorkspaceFormState } from './workspace-form.state';
import { WorkspaceDetailData, WorkspaceDetailState } from './workspace-detail.state';
import { RETRIEVE_WORKSPACE_QUERY } from './workspace-detail.effects';

export const UPDATE_WORKSPACE_HANDLER = new InjectionToken<IUpdateWorkspaceCommand>('UPDATE_WORKSPACE_HANDLER');

@Injectable()
export class WorkspaceFormEffects extends ComponentStore<Record<string, never>> {
  /**
   * Emits the saved workspace id on success, or an error message on failure.
   * Used by the facade to orchestrate post-save reload + navigation.
   */
  private readonly _saveResult$ = new Subject<{ workspaceId?: string; error?: string }>();
  public readonly saveResult$ = this._saveResult$.asObservable();

  constructor(
    private readonly formState: WorkspaceFormState,
    private readonly detailState: WorkspaceDetailState,
    @Inject(RETRIEVE_WORKSPACE_QUERY) private readonly retrieveQuery: IRetrieveWorkspaceQuery,
    @Inject(UPDATE_WORKSPACE_HANDLER) private readonly updateHandler: IUpdateWorkspaceCommand,
    private readonly authService: AuthService
  ) {
    super({});
  }

  // ============================================================================
  // Selectors — re-exposed from WorkspaceFormState
  // ============================================================================

  public get draft$() {
    return this.formState.draft$;
  }
  public get mode$() {
    return this.formState.mode$;
  }
  public get organizationId$() {
    return this.formState.organizationId$;
  }
  public get workspaceId$() {
    return this.formState.workspaceId$;
  }
  public get isNewWorkspace$() {
    return this.formState.isNewWorkspace$;
  }
  public get isEditingWorkspace$() {
    return this.formState.isEditingWorkspace$;
  }
  public get isValid$() {
    return this.formState.isValid$;
  }
  public get hasUnsavedChanges$() {
    return this.formState.hasUnsavedChanges$;
  }
  public get saving$() {
    return this.formState.saving$;
  }
  public get savingError$() {
    return this.formState.savingError$;
  }

  // ============================================================================
  // Updater passthroughs
  // ============================================================================

  public readonly updateDraft = this.formState.updateDraft;
  public readonly clearDraft = () => this.formState.clear();

  // ============================================================================
  // Effects
  // ============================================================================

  public readonly initializeNewWorkspace = this.formState.effect((organizationId$: Observable<string | undefined>) =>
    organizationId$.pipe(
      tap((organizationId: string | undefined) => {
        this.detailState.clear();
        this.formState.initializeForNew(organizationId);
      })
    )
  );

  public readonly loadDraftFromDetail = this.formState.effect((workspaceId$: Observable<string>) =>
    workspaceId$.pipe(
      tap(() => {
        this.detailState.setLoading(true);
        this.formState.setSavingError(undefined);
      }),
      switchMap((workspaceId) =>
        from(this.retrieveQuery.execute(workspaceId)).pipe(
          tap((workspace) => {
            if (!workspace) {
              this.detailState.setError('workspaces.detail.error.loading');
              return;
            }
            const detailData: WorkspaceDetailData = {
              id: workspace.id,
              name: workspace.name,
              organizationId: workspace.organizationId,
              caseTypeId: workspace.caseTypeId,
              caseTypeName: workspace.caseTypeName,
              updatedAt: workspace.referenceDate,
              usedStorage: workspace.usedStorage,
              config: workspace.config,
              numMembers: workspace.numMembers,
              numCases: workspace.numCases,
            };
            this.detailState.setData({
              id: workspace.id,
              organizationId: workspace.organizationId,
              data: detailData,
            });
            this.formState.initializeForEdit({
              workspaceId: workspace.id,
              organizationId: workspace.organizationId,
              draft: {
                name: workspace.name,
                caseTypeId: workspace.caseTypeId,
                config: workspace.config,
              },
            });
          }),
          catchError(() => {
            this.detailState.setError('workspaces.detail.error.loading');
            return of(undefined);
          })
        )
      )
    )
  );

  public readonly save = this.formState.effect((trigger$: Observable<{ organizationId: string }>) =>
    trigger$.pipe(
      tap(() => {
        this.formState.setSavingError(undefined);
        this.formState.setSaving(true);
      }),
      concatLatestFrom(() => [this.formState.draft$, this.formState.workspaceId$]),
      switchMap(([{ organizationId }, draft, workspaceId]) => {
        if (!draft) {
          this.formState.setSavingError('workspaces.edit.error.no_draft');
          this._saveResult$.next({ error: 'workspaces.edit.error.no_draft' });
          return of(undefined);
        }
        const userId = this.authService.currentUser?.id ?? '';
        return from(
          this.updateHandler.execute({
            workspaceId,
            createdById: userId,
            name: draft.name,
            organizationId,
            caseTypeId: draft.caseTypeId,
            config: draft.config,
          })
        ).pipe(
          tap((savedId: string | undefined) => {
            if (!savedId) {
              this.formState.setSavingError('workspaces.edit.error.saving');
              this._saveResult$.next({ error: 'workspaces.edit.error.saving' });
              return;
            }
            // Optimistic update of detail data
            this.detailState.mergeData({
              name: draft.name,
              caseTypeId: draft.caseTypeId,
              config: draft.config,
            });
            // Reset dirty marker against the freshly saved values
            this.formState.setSnapshot(draft);
            // For "new" mode, transition to "edit" with the new workspace id
            if (!workspaceId) {
              this.formState.initializeForEdit({
                workspaceId: savedId,
                organizationId,
                draft,
              });
            }
            this.formState.setSaving(false);
            this._saveResult$.next({ workspaceId: savedId });
          }),
          catchError((error) => {
            console.error(error);
            this.formState.setSavingError('workspaces.edit.error.saving');
            this._saveResult$.next({ error: 'workspaces.edit.error.saving' });
            return of(undefined);
          })
        );
      })
    )
  );
}
